/**
 * expenseCorrection — reine Korrektur-Builder-Logik für das Expense-Storno-Feature
 * ("Ausgabe stornieren/korrigieren", architecture-expense-storno.md).
 *
 * Reine, deterministische Transformation OHNE Seiteneffekt (KEINE Firebase-/
 * React-Imports) — voll unit-testbar. Erzeugt aus einer gebuchten Original-
 * Ausgabe eine eigenständige, signiert negierte Gegenbuchung (neues
 * `expenses`-Dokument) — das Original wird NIE geschrieben (GoBD-Immutabilität,
 * architecture §2/§8).
 *
 * Kern-Invariante (architecture §5): `netCents`, `vatOwedCents`,
 * `vatDeductibleCents`, `grossCents` sowie jeder `vatBreakdown[]`-Eintrag werden
 * gegenüber dem Original NEGIERT (× −1). Das reicht aus, damit sowohl der
 * DATEV-Export (`datevExport.ts::processExpense`, S/H-Flip via
 * `grossCents < 0`) als auch das Vorsteuer-Cockpit (`expenseSummary.ts`,
 * vorzeichen-aware Summierung) OHNE Sonderfall korrekt reagieren.
 *
 * Feld-Klassifizierung dieses Builders (Entscheidung, siehe changelog):
 *   - NEGIERT: netCents, vatOwedCents, vatDeductibleCents, grossCents, jeder
 *     vatBreakdown[]-Eintrag (netCents/vatOwedCents/vatDeductibleCents).
 *   - 1:1 GEKLONT (steuert DATEV-Mapper + Cockpit exakt, architecture §6.1):
 *     documentTypeCode, taxScheme, vatRate, vatDeductible, skrAccount,
 *     completenessStatus, supplierName, invoiceNumber.
 *   - 1:1 GEKLONT (Identitäts-/Audit-Kontext, konservative Default-Wahl bei
 *     fehlender expliziter Spec-Vorgabe): supplierVatId, supplierTaxId,
 *     missingMandatoryFields, serviceDate, category, taxSchemeConfirmedByUser,
 *     nonEurCurrency, bewirtung, roundingDiffCents.
 *   - NEU GESETZT: expenseDate=correctionDate, status='booked',
 *     correctionOfExpenseId, correctionType, correctionReason, correctionDate,
 *     vatReversed, privatentnahmeAccount.
 *   - ZURÜCKGESETZT (Korrektur ist ein neuer, eigenständiger Buchungssatz — kein
 *     Re-Parse/Re-Scan, keine offene Fälligkeit): dueDate=null,
 *     sourceReceivedInvoiceId=null, source='manual', sourceScannedReceiptId und
 *     receiptStorageRef bleiben unset (die Nachvollziehbarkeit zum Original-Beleg
 *     läuft über `correctionOfExpenseId`, nicht über eine doppelte Quellverknüpfung).
 *
 * @module expenseCorrection
 */

import { CreateExpenseInput, Expense, ExpenseVatBreakdownEntry } from '../types/expenseTypes';
import { DATEV_PRIVATENTNAHME_ACCOUNTS, SkrKontenrahmen } from './datevMapping';

// ─── isCorrectionDoc / indexCorrections / isCorrected ──────────────────────────

/**
 * Diskriminator: ist dieses Dokument selbst eine Korrektur (Gegenbuchung)?
 *
 * Fail-closed-Lesung: `correctionOfExpenseId` muss ein nicht-leerer String sein.
 * Altbestand/normale Ausgaben tragen das Feld nicht → `false`.
 *
 * @param e - Die zu prüfende Ausgabe.
 * @returns `true`, wenn `e` eine Korrektur einer anderen Ausgabe ist.
 */
export function isCorrectionDoc(e: Expense): boolean {
  return typeof e.correctionOfExpenseId === 'string' && e.correctionOfExpenseId.trim().length > 0;
}

/**
 * Indiziert ein geladenes Ausgaben-Set nach Original-ID → zugehörige Korrektur.
 *
 * Reine Ableitung (kein Firebase-Zugriff) — der Aufrufer (`ExpensesPage`) hat das
 * Set bereits vollständig geladen (`getExpensesForUser`). Bei mehreren Korrekturen
 * desselben Originals (sollte durch die Einmal-Korrektur-Invariante verhindert
 * sein — client-seitig, siehe architecture §8/§18) gewinnt die zuletzt in `all`
 * vorkommende Korrektur (Map-Overwrite-Semantik).
 *
 * @param all - Vollständiges, geladenes Ausgaben-Set eines Scopes.
 * @returns Map von Original-`expenseId` → zugehöriges Korrektur-Doc.
 */
export function indexCorrections(all: Expense[]): Map<string, Expense> {
  const idx = new Map<string, Expense>();
  for (const e of all) {
    if (isCorrectionDoc(e)) {
      idx.set(e.correctionOfExpenseId as string, e);
    }
  }
  return idx;
}

/**
 * Prüft anhand des vorab gebauten Index, ob eine Original-Ausgabe bereits
 * korrigiert wurde.
 *
 * @param expenseId - Firestore-Doc-ID der zu prüfenden (Original-)Ausgabe.
 * @param idx - Von {@link indexCorrections} gebauter Index.
 * @returns `true`, wenn eine Korrektur mit `correctionOfExpenseId === expenseId` existiert.
 */
export function isCorrected(expenseId: string, idx: Map<string, Expense>): boolean {
  return idx.has(expenseId);
}

// ─── buildCorrectionInput ───────────────────────────────────────────────────────

/** Eingabeparameter für {@link buildCorrectionInput}. */
export interface BuildCorrectionParams {
  /** Die zu korrigierende Original-Ausgabe. Muss `status==='booked'` sein und darf selbst keine Korrektur sein. */
  original: Expense;
  /** Korrektur-Variante — A (Voll-Storno) oder B (Privatentnahme). */
  correctionType: 'storno' | 'privatentnahme';
  /** Pflicht-Freitext-Begründung (Belegprinzip). Wird getrimmt; darf nach dem Trimmen nicht leer sein. */
  reason: string;
  /** ISO-Korrekturdatum (YYYY-MM-DD) — wird zu `expenseDate` der Korrektur. */
  correctionDate: string;
  /** Gewählter Kontenrahmen (aus `sellerData.datevSkr`) — bestimmt das Privatentnahme-Konto. */
  skr: SkrKontenrahmen;
}

/**
 * Baut das `CreateExpenseInput` für eine Korrektur-Gegenbuchung aus einer
 * gebuchten Original-Ausgabe. Rein & total innerhalb ihrer Vorbedingungen —
 * wirft NUR bei Verletzung invarianter Vorbedingungen (siehe unten), niemals bei
 * gültiger Eingabe.
 *
 * Setzt (siehe Modul-Kopfkommentar für die vollständige Feld-Klassifizierung):
 *   - Negierte Beträge: `netCents/vatOwedCents/vatDeductibleCents/grossCents`
 *     sowie jeder `vatBreakdown[]`-Eintrag × −1.
 *   - `privatentnahmeAccount = DATEV_PRIVATENTNAHME_ACCOUNTS[skr]` NUR bei
 *     `correctionType==='privatentnahme'`, sonst `null`.
 *   - `vatReversed = original.vatDeductibleCents !== 0`.
 *   - `status='booked'`, `expenseDate=correctionDate`.
 *   - `correctionOfExpenseId=original.id`, `correctionType`,
 *     `correctionReason=reason.trim()`, `correctionDate`.
 *
 * `correctionCreatedByUid` wird HIER bewusst NICHT gesetzt — dieser Builder ist
 * rein (kein Auth-Zugriff). Der API-Wrapper (`api/expensesApi.ts::createCorrectionExpense`)
 * setzt das Feld nach dem Aufruf dieser Funktion.
 *
 * @throws {Error} Wenn `original.status !== 'booked'` (nur gebuchte Ausgaben sind korrigierbar).
 * @throws {Error} Wenn `original` selbst bereits eine Korrektur ist (`isCorrectionDoc`) —
 *   Einmal-Korrektur-Invariante, „eine Korrektur ist nicht korrigierbar" (architecture §8/§18).
 * @throws {Error} Wenn `reason.trim()` leer ist (Pflicht-Begründung, Belegprinzip).
 * @throws {Error} Wenn `correctionDate` leer/whitespace ist.
 */
export function buildCorrectionInput(params: BuildCorrectionParams): CreateExpenseInput {
  const { original, correctionType, reason, correctionDate, skr } = params;

  if (original.status !== 'booked') {
    throw new Error(
      `expenseCorrection.buildCorrectionInput: original expense must be booked to be corrected ` +
        `(got status="${original.status}").`,
    );
  }
  if (isCorrectionDoc(original)) {
    throw new Error(
      'expenseCorrection.buildCorrectionInput: cannot correct a correction document ' +
        '(original already carries a non-empty correctionOfExpenseId).',
    );
  }

  const trimmedReason = reason.trim();
  if (trimmedReason.length === 0) {
    throw new Error(
      'expenseCorrection.buildCorrectionInput: correctionReason must be a non-empty string.',
    );
  }

  const trimmedDate = (correctionDate ?? '').trim();
  if (trimmedDate.length === 0) {
    throw new Error(
      'expenseCorrection.buildCorrectionInput: correctionDate must be a non-empty ISO date string.',
    );
  }

  const negatedVatBreakdown: ExpenseVatBreakdownEntry[] = original.vatBreakdown.map((entry) => ({
    categoryCode: entry.categoryCode,
    taxRate: entry.taxRate,
    netCents: -entry.netCents,
    vatOwedCents: -entry.vatOwedCents,
    vatDeductibleCents: -entry.vatDeductibleCents,
  }));

  const privatentnahmeAccount: string | null =
    correctionType === 'privatentnahme' ? DATEV_PRIVATENTNAHME_ACCOUNTS[skr] : null;

  const vatReversed = original.vatDeductibleCents !== 0;

  // Bewirtung wird nur geklont, wenn sie am Original vorhanden ist (siehe Spread
  // unten) — verhindert einen materialisierten undefined-Key im Firestore-Write.
  const clonedBewirtung = original.bewirtung;

  const input: CreateExpenseInput = {
    // ─── Supplier — 1:1 geklont (Identitäts-Kontext) ──────────────────────────
    supplierName: original.supplierName,
    supplierVatId: original.supplierVatId,
    supplierTaxId: original.supplierTaxId,
    invoiceNumber: original.invoiceNumber,
    documentTypeCode: original.documentTypeCode,

    // ─── Beträge — negiert (Kern-Invariante) ──────────────────────────────────
    netCents: -original.netCents,
    vatOwedCents: -original.vatOwedCents,
    vatDeductibleCents: -original.vatDeductibleCents,
    grossCents: -original.grossCents,
    vatRate: original.vatRate,
    vatBreakdown: negatedVatBreakdown,
    roundingDiffCents: original.roundingDiffCents,

    // ─── Tax-Klassifikation — 1:1 geklont ──────────────────────────────────────
    taxScheme: original.taxScheme,
    taxSchemeConfirmedByUser: original.taxSchemeConfirmedByUser,
    vatDeductible: original.vatDeductible,

    // ─── §14-Vollständigkeit — 1:1 geklont ─────────────────────────────────────
    completenessStatus: original.completenessStatus,
    missingMandatoryFields: [...original.missingMandatoryFields],

    // ─── Dates — Korrekturdatum neu, Rest geklont/zurückgesetzt ────────────────
    expenseDate: trimmedDate,
    dueDate: null, // Korrektur ist bereits gebucht — keine eigene Fälligkeit.
    serviceDate: original.serviceDate,

    // ─── Klassifikation/Routing — geklont ───────────────────────────────────────
    category: original.category,
    skrAccount: original.skrAccount,

    // ─── Source — zurückgesetzt (Korrektur ist kein Re-Parse/Re-Scan) ──────────
    sourceReceivedInvoiceId: null,
    source: 'manual',
    // sourceScannedReceiptId/receiptStorageRef bewusst NICHT gesetzt (undefined) —
    // die Nachvollziehbarkeit läuft über correctionOfExpenseId, nicht über eine
    // doppelte Quellverknüpfung (die einen falschen Dedup-Treffer erzeugen könnte).

    // ─── Bewirtung — 1:1 geklont NUR falls vorhanden ───────────────────────────
    // Conditional-Spread (Muster wie ReceiptScanModal): bei jeder Nicht-
    // Bewirtungs-Ausgabe (Regelfall) fehlt die Bewirtungs-Info. Ein
    // materialisierter undefined-Wert ließe den nachgelagerten addDoc hart
    // werfen (Firestore lehnt undefined ab) — der Key MUSS abwesend sein.
    // Beeinflusst NIE die Vorsteuer.
    ...(clonedBewirtung ? { bewirtung: clonedBewirtung } : {}),

    nonEurCurrency: original.nonEurCurrency,

    // ─── Lifecycle ───────────────────────────────────────────────────────────
    status: 'booked',

    // ─── Korrektur-Metadaten ─────────────────────────────────────────────────
    correctionOfExpenseId: original.id,
    correctionType,
    correctionReason: trimmedReason,
    correctionDate: trimmedDate,
    vatReversed,
    privatentnahmeAccount,
    // correctionCreatedByUid: bewusst NICHT gesetzt — siehe JSDoc oben.
  };

  return input;
}
